"use client";

import Link from "next/link";

import { persistAppLocaleCookie } from "@/config/app-locale";

import { MarketingFooter, MarketingHeader } from "./marketing-home";
import type { MarketingLocale } from "../content/marketing-copy";
import { pricingCopy } from "../content/pricing-copy";

import marketingStyles from "./marketing-home.module.css";
import styles from "./pricing-page.module.css";

const LOGIN_PATH = "/12connex/login";

type PricingPageProps = {
  locale: MarketingLocale;
};

export function PricingPage({ locale }: PricingPageProps) {
  const copy = pricingCopy[locale];

  return (
    <div className={marketingStyles.page}>
      <MarketingHeader locale={locale} linkToHomeSections localePath="/pricing" />
      <main className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.badge}><span aria-hidden="true" />{copy.badge}</p>
            <h1>{copy.title}</h1>
            <p className={styles.lead}>{copy.description}</p>
            <div className={styles.heroActions}>
              <Link href={LOGIN_PATH} className={styles.primaryButton} onClick={() => persistAppLocaleCookie(locale)}>{copy.create}</Link>
              <a href="#compare" className={styles.ghostButton}>{copy.compareAction} ↓</a>
            </div>
          </div>
        </section>

        <section className={styles.compareSection} id="compare">
          <div className={styles.sectionInner}>
            <h2>{copy.compareTitle}</h2>
            <div className={styles.comparison} role="table" aria-label={copy.compareTitle}>
              <div className={`${styles.comparisonRow} ${styles.comparisonHead}`} role="row">
                {copy.columns.map((column, index) => (
                  <div role="columnheader" key={column} className={index === 2 ? styles.highlight : undefined}>
                    {index === 2 ? <FullAccessHeading label={column} /> : column}
                  </div>
                ))}
              </div>
              {copy.rows.map(([feature, free, full], index) => (
                <div className={styles.comparisonRow} role="row" key={feature}>
                  <div role="rowheader">{feature}</div>
                  <div role="cell"><ComparisonStatus variant={freeStatus[index]} />{free}</div>
                  <div role="cell" className={styles.highlight}><ComparisonStatus variant="check" />{full}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.includedSection}>
          <div className={styles.sectionInner}>
            <p className={styles.eyebrow}><span className={styles.includedCheck} aria-hidden="true">✓</span>{copy.includedEyebrow}</p>
            <h2>{copy.includedTitle}</h2>
            <div className={styles.benefitGrid}>
              {copy.benefits.map(([title, description]) => (
                <article key={title}><h3>{title}</h3><p>{description}</p></article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.pricingSection} id="pricing">
          <div className={styles.pricingInner}>
            <h2>{copy.pricingTitle}</h2>
            <article className={styles.priceCard}>
              <div className={styles.priceCardHead}>
                <p>{copy.access}</p>
                <div><strong>{copy.price}</strong><span>{copy.taxNote}</span></div>
                <p>{copy.duration}</p>
              </div>
              <div className={styles.priceCardBody}>
                <p className={styles.bilingual}><span aria-hidden="true">🌐</span>{copy.bilingual}</p>
                <Link href={LOGIN_PATH} className={styles.primaryButton} onClick={() => persistAppLocaleCookie(locale)}>{copy.create}</Link>
                <p className={styles.note}>{copy.signInNote}</p>
                <p className={styles.note}>{copy.note}</p>
              </div>
            </article>
            <ul className={styles.trustList}>
              {copy.trust.map((item, index) => (
                <li key={item}><span aria-hidden="true">{["🔒", "🍁", "⚡", "🌐"][index]}</span>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <MarketingFooter locale={locale} linkToHomeSections />
    </div>
  );
}

const freeStatus = ["check", "check", "check", "check", "lock", "lock", "excluded", "lock", "none"] as const;

function ComparisonStatus({ variant }: { variant: (typeof freeStatus)[number] }) {
  if (variant === "none") return null;
  if (variant === "lock") return <span className={styles.lockStatus} aria-hidden="true">🔒</span>;
  if (variant === "excluded") return <span className={styles.excludedStatus} aria-hidden="true">×</span>;
  return <span className={styles.checkStatus} aria-hidden="true">✓</span>;
}

function FullAccessHeading({ label }: { label: string }) {
  const match = label.match(/^(.*) \((.*)\)$/);

  if (!match) return label;

  return <><strong>{match[1]}</strong><span>{match[2]}</span></>;
}
